import { Config } from '@app/src/config'
import type { SolutionType } from '../types/resourceExplorer'

interface NotificationSeverityTypes {
  success: 'success'
  error: 'error'
  info: 'info'
  warning: 'warning'
}

export const ConstVars = {
  SupplierAccessors: {
    Name: 'name',
    RegistrationNumber: 'registrationNumber',
    VatNumber: 'vatNumber',
    GlnNumber: 'glnNumber',
    Country: 'country.countryCode',
    CustomId: 'customId',
    WebsiteAddress: 'websiteAddress',
    LinkStatus: 'linkStatus',
    CreatedAt: 'createdAt',
    LinkedOrganization: 'organization',
    LinkedRegistrationNumber: 'organization.registrationNumber',
    LinkedVatNumber: 'organization.vatNumber',
    LinkedGlnNumber: 'organization.glnNumber',
    LinkedPrimaryAddress: 'organization.locations',
    LinkedWebsiteAddress: 'organization.websiteAddress',
    ReferralContact: 'referralContact',
    PublicContact: 'publicContact',
    ProviderApprovalStatus: 'providerApprovalStatus',
    ProviderActivityStatus: 'providerActivityStatus',
    FinalRiskRating: 'finalRiskRating',
    ProviderPriority: 'providerPriority',
    SupplierUsage: 'supplierUsage',
    Tier: 'tier',
    CustomCategory: 'categoryOptions.name',
    Duns: 'duns',
  },
  ContactsAccessors: {
    Contact: '*',
    Organization: 'creatorOrganization',
    Email: 'user.email',
    Position: 'position',
    Phone: 'phoneNumber',
    Linked: 'linked',
    CreatedAt: 'createdAt',
  },
  MappingNodeAccessors: {
    MappingNode: '*',
  },
  UsersAccessors: {
    Name: 'name',
    Email: 'email',
    Position: 'position',
    Phone: 'phoneNumber',
    CreatedAt: 'createdAt',
  },
  ResponseItemsAccessors: {
    Sender: 'response.request.creatorOrganization',
    RequestTemplate: 'response.request.title',
    Responder: 'response.request.responderObject',
    Object: 'requestItem.description',
    Target: 'response.request.targetObject.name',
    TargetType: 'response.request.targetObjectType',
    HasAnswered: 'isAnswered',
    AnswerSet: 'isValueSet',
    Answer: 'answer',
    Unit: 'unit',
    ResponseDate: 'response.submittedAt',
    InternalVerificationStatus: 'internalVerificationStatus',
    InternalVerificationComment: 'internalVerificationComment',
    ExternalVerificationStatus: 'externalVerificationStatus',
    ExternalVerificationComment: 'externalVerificationComment',
    ResponseInternalVerificationStatus: 'response.internalVerificationStatus',
    ResponseInternalVerificationComment: 'response.internalVerificationComment',
    ResponseExternalVerificationStatus: 'response.externalVerificationStatus',
    ResponseExternalVerificationComment: 'response.externalVerificationComment',
    ExpiresAt: 'expiresAt',
    PeriodDisplayName: 'requestItem.periodDisplayName',
    PeriodStartsAt: 'requestItem.periodStartsAt',
    PeriodEndsAt: 'requestItem.periodEndsAt',
    QuestionType: 'requestItem.questionType.name',
    Section: 'requestItem.section.title',
    RequestItemTemplateId: 'requestItem.questionId',
    ActivityStatus: 'response.request.subscriptions[0].target.activityStatus',
    FinalRiskRating: 'response.request.subscriptions[0].target.finalRiskRating',
    Priority: 'response.request.subscriptions[0].target.priority',
    ApprovalStatus: 'response.request.subscriptions[0].target.providerApprovalStatus',
    Tier: 'response.request.subscriptions[0].target.tier',
    SupplierUsage: 'response.request.subscriptions[0].target.supplierUsage',
    CustomCategory: 'response.request.subscriptions[0].target.categoryOptions.name',
    CustomId: 'response.request.subscriptions[0].target.customId',
    Country: 'response.request.subscriptions[0].target.country.name',
    Duns: 'response.request.subscriptions[0].target.duns',
    LinkStatus: 'response.request.subscriptions[0].target.linkStatus',
    Comment: 'comment',
  },
  OrganizationAccessors: {
    Name: 'name',
    RegistrationNumber: 'registrationNumber',
    VatNumber: 'vatNumber',
    GlnNumber: 'glnNumber',
    Country: 'country.countryCode',
    PrimaryAddress: 'location.formattedAddress',
    WebsiteAddress: 'websiteAddress',
  },
  RequestTemplatesAccessors: {
    Title: 'title',
    Creator: 'creatorOrganization',
    CreationDate: 'createdAt',
  },
  AuditLogsAccessors: {
    CreatedAt: 'createdAt',
    ServiceType: 'serviceType',
    SourceEventType: 'sourceEventType',
    SourceObjectType: 'objectType',
    BeforeSnapshot: 'beforeSnapshot',
    AfterSnapshot: 'afterSnapshot',
    CreatorUser: 'creatorUser.name',
    CreatorOrganization: 'creatorOrganization.name',
    LogIdentity: 'logIdentity.userId',
  },
  NotificationAccessors: {
    CreatorOrganization: 'creatorOrganization',
    Service: 'service',
    Event: 'sourceEventType',
    ObjectTitle: 'objectTitle',
    NotificationDate: 'createdAt',
    Seen: 'seen',
    Link: 'link',
    Object: 'details.title',
  },
  ColleaguesAccessors: {
    Name: 'name',
    Email: 'email',
    Position: 'position',
    Phone: 'phoneNumber',
    CreatedAt: 'createdAt',
    Role: 'role',
  },
  InvitationAccessors: {
    Name: 'name',
    Email: 'email',
    Role: 'role',
    Status: 'status',
    CreatedAt: 'createdAt',
  },
  SubscriptionAccessors: {
    CreatorOrganization: 'creatorOrganization',
    RequestTitle: 'request.title',
    CreatedAt: 'createdAt',
  },
  EvaluationAccessors: {
    Target: 'targetObject.name',
    FrameworkSection: 'indicator.section.title',
    FrameworkIndication: 'indicator.description',
    Value: 'value',
    Unit: 'indicator.unit.symbol',
    Period: 'periodStartsAt',
    Evidence: 'evidence',
    Framework: 'indicator.section.framework.title',
    CreatedAt: 'createdAt',
  },
  RiskAccessors: {
    Name: 'parentObject.name',
    Country: 'country.name',
  },
}

export const NotificationSeverity: NotificationSeverityTypes = {
  success: 'success',
  error: 'error',
  info: 'info',
  warning: 'warning',
}

export const DEFAULT_TABLE_PAGE_SIZE = 50

export const MIN_FILTER_OPTIONS_TO_SHOW_SEARCH_BAR = 10

export const DEFAULT_LANGUAGE = 'en-US'

export const DEFAULT_CHART_COLORS = ['#3b7eb8', '#1abc9c', '#f39c12', '#34495e', '#bdc3c7', '#9b59b6']

export const GOOGLE_MAPS_API_KEY = 'AIzaSyCtsjwyYnglUqiZe9ws0hxKs36-oJZWwu0'

export const MAX_PAGE_CONTENT_WIDTH = 1195

export const WF_SYSTEM_USER = 1
export const WF_ORGANIZATION_ID = 1
export const WF_EMAIL_DOMAIN = '@worldfavor.com'

export const ORGANIZATION_NAME_REGEX = /^[A-Za-zÀ-ÿ0-9\s.,&'"\-/:()]+$/

export const TIMEZONE_COUNTRY_MAP: Record<string, string> = {
  // North America
  'America/New_York': 'US',
  'America/Los_Angeles': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Montreal': 'CA',
  'America/Mexico_City': 'MX',
  // Europe - Nordic
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Copenhagen': 'DK',
  'Europe/Helsinki': 'FI',
  'Europe/Reykjavik': 'IS',
  // Europe - Western
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR',
  'Europe/Brussels': 'BE',
  'Europe/Amsterdam': 'NL',
  'Europe/Zurich': 'CH',
  'Europe/Luxembourg': 'LU',
  // Europe - Central
  'Europe/Berlin': 'DE',
  'Europe/Vienna': 'AT',
  'Europe/Prague': 'CZ',
  'Europe/Warsaw': 'PL',
  'Europe/Budapest': 'HU',
  // Europe - Southern
  'Europe/Madrid': 'ES',
  'Europe/Barcelona': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Lisbon': 'PT',
  'Europe/Athens': 'GR',
  // Europe - Eastern
  'Europe/Bucharest': 'RO',
  'Europe/Sofia': 'BG',
  'Europe/Tallinn': 'EE',
  'Europe/Riga': 'LV',
  'Europe/Vilnius': 'LT',
  // Asia - East
  'Asia/Tokyo': 'JP',
  'Asia/Seoul': 'KR',
  'Asia/Shanghai': 'CN',
  'Asia/Hong_Kong': 'HK',
  'Asia/Taipei': 'TW',
  // Asia - Southeast
  'Asia/Singapore': 'SG',
  'Asia/Bangkok': 'TH',
  'Asia/Jakarta': 'ID',
  'Asia/Manila': 'PH',
  'Asia/Kuala_Lumpur': 'MY',
  // Asia - South
  'Asia/Kolkata': 'IN',
  'Asia/Dubai': 'AE',
  'Asia/Karachi': 'PK',
  // Oceania
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Pacific/Auckland': 'NZ',
  // South America
  'America/Sao_Paulo': 'BR',
  'America/Buenos_Aires': 'AR',
  'America/Santiago': 'CL',
  'America/Lima': 'PE',
  'America/Bogota': 'CO',
}

export const HALF_MINUTE_MS = 30 * 1000
export const ONE_MINUTE_MS = 60 * 1000
export const TWO_MINUTES_MS = ONE_MINUTE_MS * 2
export const FIVE_MINUTES_MS = ONE_MINUTE_MS * 5
export const ONE_HOUR_MS = ONE_MINUTE_MS * 60
export const TWELVE_HOURS_MS = ONE_HOUR_MS * 12

export const DefaultImages = {
  User: 'https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcSS7BpholXJ1BnoyDTPEMHBVmLleGWnUYJAvA&usqp=CAU',
  Organization: `${Config.STORAGE_ACCOUNT_URL}/app-imageuploads/default_organization_image.png`,
  SignupFlow: `${Config.STORAGE_ACCOUNT_URL}/app-imageuploads/worldfavor-image-workers-on-field.png`,
  SourcingPromo: `${Config.STORAGE_ACCOUNT_URL}/app-imageuploads/sourcing-promo.png`,
  SourcingPromoCardIso: '/img/lead-promo/iso.png',
  SourcingPromoCardWarning: '/img/lead-promo/WarningOutlined.svg',
}

export const Solutions = {
  Investment: 'investing',
  Sourcing: 'sourcing',
  SourcingStart: 'sourcingstart',
  Resources: 'resources',
  Transparency: 'transparency',
  Management: 'management',
  Finance: 'finance',
}

export enum Solution {
  TRANSPARENCY = 'transparency',
  INVESTMENT = 'investing',
  SOURCING = 'sourcing',
  FINANCE = 'finance',
  MANAGEMENT = 'management',
  OTHER = 'other',
}

export enum AccountType {
  TRANSPARENCY = 'transparency',
  INVESTMENT = 'investing',
  SOURCING = 'sourcing',
  SOURCINGSTART = 'sourcingStart',
  FINANCE = 'finance',
  MANAGEMENT = 'management',
}

export const SolutionTitles = {
  Investment: 'Sustainable Investments',
  Sourcing: 'Sustainable Sourcing',
  SourcingStart: 'Sustainable Sourcing Start',
  Transparency: 'Transparency Solution',
  Management: 'Sustainability Management',
  Finance: 'ESG Reporting',
  Default: 'Admin tools',
}

export const SolutionsIds = {
  Investing: 1,
  Sourcing: 2,
  Transparency: 3,
}

export const Roles = {
  WfAdmin: 'worldfavoradmin',
  WfEmployeeAdmin: 'worldfavoremployeeadmin',
  Admin: 'admin',
  Contributor: 'contributor',
  ViewOnly: 'viewonly',
}
export const ADMIN_ROLES = [Roles.Admin, Roles.WfAdmin, Roles.WfEmployeeAdmin]
export type Role = (typeof Roles)[keyof typeof Roles]

export const Features = {
  Verification: 'Verification',
}
export type Feature = (typeof Features)[keyof typeof Features]

export const Flows = {
  Interaction: 'interaction',
  Verification: 'verification',
  Overview: 'overview',
  Reporting: 'reporting',
  ManageRequests: 'manageRequests',
}

// Without `as const` TypeScript widens these three to string, and ProviderType below would accept any string.
export const ResourceTypes = {
  Organization: 'organization',
  Investee: 'investee' as const,
  Company: 'company' as const,
  Contact: 'contact',
  User: 'user',
  Supplier: 'supplier' as const,
  Provider: 'provider',
  Request: 'request',
  Inquiry: 'inquiry',
  Response: 'response',
  ResponseScraping: 'response-scraping',
  Overview: 'overview',
  ResponseItem: 'responseItem',
  Answer: 'response/items',
  RequestTemplate: 'requestTemplate',
  AuditLog: 'auditLog',
  Colleague: 'colleague',
  Notification: 'notification',
  Password: 'password',
  Invitation: 'invitation',
  Subscription: 'subscription',
  Indicator: 'indicator',
  Framework: 'framework',
  DataPoint: 'datapoint',
  AccessibleOrganizations: 'accessibleOrganizations',
  Claim: 'claim',
  RiskScreening: 'risk-screening',
  Category: 'category',
  ControlPanel: 'controlPanel',
  CustomReports: 'customReports',
  StandardReports: 'standardReports',
  ReportTable: 'reportTable',
  Product: 'product',
  MappingNode: 'mappingNode',
  ProductMappingRequest: 'productMappingRequest',
  ProductMappingResponse: 'productMappingResponse',
  Assessment: 'assessment',
  ResponseItemAnswer: 'responseItemAnswer',
}

// The three provider nouns an accessor can be shown. Picks the wording in translations, not shown as-is.
export type ProviderType = typeof ResourceTypes.Supplier | typeof ResourceTypes.Investee | typeof ResourceTypes.Company

export const PERCENTAGE_UNIT_ID = 113
export const PERCENTAGE_UNIT_SYMBOL = '%'

export const ResourceTypesIds: { [key: string]: number } = {
  Investee: 137,
  Supplier: 113,
}

export enum SolutionTourIds {
  default = 244236,
  transparency = 442851,
  sourcing = 271787,
  sourcingstart = 373705,
  investing = 280401,
  finance = 277449,
}

export const QuestionTypes = {
  Text: 'Text',
  Number: 'Number',
  Options: 'Options',
  File: 'File',
  Coordinates: 'Coordinates',
}

export enum WfRequestTags {
  PublicRequest1 = 'WfDefaultRequest1',
  PublicRequest2 = 'WfDefaultRequest2',
  PublicRequest3 = 'WfDefaultRequest3',
}

export enum EsgRequestTags {
  Environmental = 'esg-environmental',
  Social = 'esg-social',
  Governance = 'esg-governance',
  Regulations = 'esg-regulations',
  Initiatives = 'esg-initiatives',
  SupplyChain = 'WfDefaultRequest2',
  Other = 'CompanyInformationRequest',
}

export enum SfdrRequestTags {
  Sfdr = 'sfdr',
  SfdrSusApproach = 'sfdr-sus-approach',
  SfdrPai1 = 'sfdr-pai-1',
  SfdrPai2a = 'sfdr-pai-2a',
  SfdrPai2b = 'sfdr-pai-2b',
  SfdrArticle8and9 = 'sfdr-article-8-9',
  WfDefaultRequest1 = 'WfDefaultRequest1',
}

export type RequestTags = WfRequestTags | EsgRequestTags | SfdrRequestTags

export const ImportTemplates: { [key: string]: string } = {
  datapoint: `${Config.STORAGE_ACCOUNT_URL}/import-templates/EvaluationImport.xlsx`,
  supplier: `${Config.STORAGE_ACCOUNT_URL}/import-templates/SupplierImport.xlsx`,
  investee: `${Config.STORAGE_ACCOUNT_URL}/import-templates/InvesteeImport.xlsx`,
  company: `${Config.STORAGE_ACCOUNT_URL}/import-templates/CompanyImport.xlsx`,
}

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250]

export const FacetTypes: {
  min: 'min'
  max: 'max'
  avg: 'avg'
  sum: 'sum'
  median: 'median'
  count: 'count'
} = {
  min: 'min',
  max: 'max',
  avg: 'avg',
  sum: 'sum',
  median: 'median',
  count: 'count',
}

export enum ResponseStatus {
  ResponseSubmitted = 'ResponseSubmitted',
  ResponseApproved = 'ResponseApproved',
  ResponseSent = 'Sent',
  ResponseCorrectionNeeded = 'ResponseCorrectionNeeded',
}

// When this is updated, also update in the backend (CategoryService/CategoryOptionService)
export const CreationLimits = {
  Categories: 30,
  CategoryOptions: 50,
}

export const ProviderPriorityValues = {
  NotSelected: 'NotSelected',
  NotAPriority: 'NotAPriority',
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  VeryHigh: 'VeryHigh',
}

export const ProviderTierValues = {
  NotSelected: 0,
  Tier1: 1,
  Tier2: 2,
  Tier3: 3,
  Tier4: 4,
}

export const ProviderUsageValues = {
  NotSelected: 'NotSelected',
  Direct: 'Direct',
  Indirect: 'Indirect',
}

export const ProviderFinalRiskRatingValues = {
  NotSelected: 'A_NotSelected',
  LowRisk: 'B_Green',
  MediumRisk: 'C_Yellow',
  HighRisk: 'D_Orange',
  ExtremeRisk: 'E_Red',
}

export const ProviderApprovalStatusValues = {
  NotSelected: 'NotSelected',
  NotApproved: 'NotApproved',
  UnderObservation: 'UnderObservation',
  ApprovedAfterConsideration: 'ApprovedAfterConsideration',
  Approved: 'Approved',
}

export const ExternalLinks = {
  TermsAndConditions: 'https://worldfavor.com/terms-conditions',
  PrivacyPolicy: 'https://worldfavor.com/privacy-policy',
  CookiePolicy: 'https://worldfavor.com/cookie-policy',
}

export const Logos = {
  WorldfavorLogoSquareFilledBlack: '/img/brand/worldfavor_logo_black_2024.svg',
}

export interface OrganizationSettings {
  autoAcceptProductMappingResponses: boolean
  currencyUnitId: number
  spendPeriodFocus?: string | null
  mediumSpendMinValue?: number | null
  mediumSpendMaxValue?: number | null
  spendIntervalType?: SpendIntervalType
  allowedProviderScreenings?: number
  contractedUsers?: number
  contractedSuppliers?: number
  totalProviderScreened?: number
  remainingProviderScreenings?: number
  solutionType: SolutionType
  screeningOnlyUseSourcesPublishedInOrAfterYear?: number | null
  aiAnswerAcceptanceLevel?: AnswerAcceptanceLevel
  aiCaptureWebPagesAsFiles?: boolean
  aiScreeningEnabled?: boolean
}

export enum AnswerAcceptanceLevel {
  Broad = 'Broad',
  Balanced = 'Balanced',
  Strict = 'Strict',
}

export enum SpendIntervalType {
  Manual = 'Manual',
  Automatic = 'Automatic',
}

export enum SpendClassification {
  NotSet = 'NotSet',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export const spendClassificationOrder = [
  SpendClassification.NotSet,
  SpendClassification.High,
  SpendClassification.Medium,
  SpendClassification.Low,
].map(s => s.toString())

export const AmplitudeTrackingEvents = {
  Provider: {
    Questionnaire: {
      ResponseStarted: 'Provider.Questionnaire.ResponseStarted',
      ResponseDiscarded: 'Provider.Questionnaire.ResponseDiscarded',
      ResponseSubmitted: 'Provider.Questionnaire.ResponseSubmitted',
      SuggestedAnswersModal: {
        Modal: 'Provider.Questionnaire.SuggestedAnswersModal',
        ContinueClicked: 'Provider.Questionnaire.SuggestedAnswersModal.ContinueClicked',
        Skipped: 'Provider.Questionnaire.SuggestedAnswersModal.Skipped',
        AutofillClicked: 'Provider.Questionnaire.SuggestedAnswersModal.AutofillClicked',
        ManualClicked: 'Provider.Questionnaire.SuggestedAnswersModal.ManualClicked',
      },
      AiSuggestedAnswers: 'Provider.Questionnaire.AiSuggestedAnswers',
      PreviousSuggestedAnswers: 'Provider.Questionnaire.PreviousSuggestedAnswers',
      PublicDataSuggestedAnswers: 'Provider.Questionnaire.PublicDataSuggestedAnswers',
      SuggestionsAutofilled: 'Provider.Questionnaire.SuggestionsAutofilled',
    },
    ConnectOnlyFlow: {
      BannerViewed: 'Provider.ConnectOnlyFlow.BannerViewed',
      BannerClicked: 'Provider.ConnectOnlyFlow.BannerClicked',
      ModalOpened: 'Provider.ConnectOnlyFlow.ModalOpened',
      ModalClosed: 'Provider.ConnectOnlyFlow.ModalClosed',
      ProviderConnected: 'Provider.ConnectOnlyFlow.ProviderConnected',
      AllProvidersConnected: 'Provider.ConnectOnlyFlow.AllProvidersConnected',
      ProviderViewed: 'Provider.ConnectOnlyFlow.ProviderViewed',
      SummaryViewed: 'Provider.ConnectOnlyFlow.SummaryViewed',
      ContinueConnectingClicked: 'Provider.ConnectOnlyFlow.ContinueConnectingClicked',
    },
    Expectation: {
      Accepted: 'Provider.Expectation.Accepted',
    },
  },
  Onboarding: {
    VerifyEmail: {
      VerifyEmailOpened: 'Onboarding.VerifyEmail.VerifyEmailOpened',
      ResentVerificationEmail: 'Onboarding.VerifyEmail.ResentVerificationEmail',
    },
    UserInfo: {
      AddUserInfoAndAcceptTandCOpened: 'Onboarding.CreateUser.AddUserInfoAndAcceptTandCOpened',
      AddUserInfoAndAcceptTandCSubmitted: 'Onboarding.CreateUser.AddUserInfoAndAcceptTandCSubmitted',
      LanguageChanged: 'Onboarding.UserInfo.LanguageChanged',
    },
    AddOrganization: {
      OpenAddOrg: 'Onboarding.AddOrganization.OpenedAddOrg',
      SubmittedForm: 'Onboarding.AddOrganization.SubmittedForm',
      OrgAlreadyExists: 'Onboarding.AddOrganization.OrgAlreadyExists',
      InvitedCompany: 'Onboarding.AddOrganization.InvitedCompany',
      OpenedOrgDetails: 'Onboarding.AddOrganization.OpenedOrgDetails',
      OpenedIndustryDetails: 'Onboarding.AddOrganization.OpenedIndustryDetails',
      OpenedPersonalDetails: 'Onboarding.AddOrganization.OpenedPersonalDetails',
      SubmittedAccessRequest: 'Onboarding.AddOrganization.SubmittedAccessRequest',
      OpenedCreateOrganizationDialog: 'Onboarding.AddOrganization.OpenedCreateOrganizationDialog',
      CreateOrganizationContinueClicked: 'Onboarding.AddOrganization.CreateOrganizationContinueClicked',
      CreateOrganizationValidationFailed: 'Onboarding.AddOrganization.CreateOrganizationValidationFailed',
      DuplicateExactShown: 'Onboarding.AddOrganization.DuplicateExactShown',
      DuplicateSimilarShown: 'Onboarding.AddOrganization.DuplicateSimilarShown',
      ConfigureAccountShown: 'Onboarding.AddOrganization.ConfigureAccountShown',
      ConfigureAccountSubmitClicked: 'Onboarding.AddOrganization.ConfigureAccountSubmitClicked',
      ConfigureAccountValidationFailed: 'Onboarding.AddOrganization.ConfigureAccountValidationFailed',
      ConfigureAccountSubmitted: 'Onboarding.AddOrganization.ConfigureAccountSubmitted',
      ConfigureAccountSubmitFailed: 'Onboarding.AddOrganization.ConfigureAccountSubmitFailed',
      ConversionCompletedBothSteps: 'Onboarding.AddOrganization.ConversionCompletedBothSteps',
    },
    SendInvite: {
      OpenedConnectFlow: 'Onboarding.SendInvite.OpenedConnectFlow',
      ReferralContactAdded: 'Onboarding.SendInvite.ReferralContactAdded',
    },
  },
  Organization: {
    UpdatePublicContact: {
      ViewedNudge: 'Organization.UpdatePublicContact.ViewedNudge',
      ContactUpdated: 'Organization.UpdatePublicContact.ContactUpdated',
      ContactDeleted: 'Organization.UpdatePublicContact.ContactDeleted',
    },
    RiskIndex: {
      Toggled: 'Organization.RiskIndex.Toggled',
    },
  },
  Accessor: {
    InviteSupplier: {
      InvitationViewed: 'Accessor.InviteSupplier.InvitationViewed',
      InvitationSent: 'Accessor.InviteSupplier.InvitationSent',
      ContactDeleted: 'Accessor.InviteSupplier.ContactDeleted',
      InvitationLinkCopied: 'Accessor.InviteSupplier.InvitationLinkCopied',
      ConnectExistingCompany: 'Accessor.InviteSupplier.ConnectExistingCompany',
    },
    SuggestedContact: {
      SubmittedRequest: 'Accessor.SuggestedContact.SubmittedRequest',
    },
    ProviderOrganization: {
      AdditionalInformation: 'Accessor.ProviderOrganization.AdditionalInformation',
    },
    RequestReminder: {
      Opened: 'Accessor.RequestReminder.Opened',
      ReminderSent: 'Accessor.RequestReminder.ReminderSent',
    },
    ResponseItemVerification: {
      ExternalVerification: {
        Opened: 'Accessor.ResponseItemVerification.ExternalVerification.Opened',
        Submitted: 'Accessor.ResponseItemVerification.ExternalVerification.Submitted',
      },
      BulkExternalVerification: {
        Opened: 'Accessor.ResponseItemVerification.BulkExternalVerification.Opened',
        Submitted: 'Accessor.ResponseItemVerification.BulkExternalVerification.Submitted',
      },
      InternalVerification: {
        Opened: 'Accessor.ResponseItemVerification.InternalVerification.Opened',
        Submitted: 'Accessor.ResponseItemVerification.InternalVerification.Submitted',
      },
      BulkInternalVerification: {
        Opened: 'Accessor.ResponseItemVerification.BulkInternalVerification.Opened',
        Submitted: 'Accessor.ResponseItemVerification.BulkInternalVerification.Submitted',
      },
    },
    ResponseVerification: {
      InternalVerification: {
        Opened: 'Accessor.ResponseVerification.InternalVerification.Opened',
        Submitted: 'Accessor.ResponseVerification.InternalVerification.Submitted',
      },
      ExternalVerification: {
        Opened: 'Accessor.ResponseVerification.ExternalVerification.Opened',
        Submitted: 'Accessor.ResponseVerification.ExternalVerification.Submitted',
      },
    },
    ExcelExport: {
      Opened: 'Accessor.ExcelExport.Opened',
      ChangedColumnNames: 'Accessor.ExcelExport.ChangedColumnNames',
      SavedExportSettings: 'Accessor.ExcelExport.SavedExportSettings',
      DeletedExportSettings: 'Accessor.ExcelExport.DeletedExportSettings',
      ExportRequested: 'Accessor.ExcelExport.ExportRequested',
    },
    Expectation: {
      AutomationCreated: 'Accessor.Expectation.AutomationCreated',
      BulkCreated: 'Accessor.Expectation.BulkCreated',
    },
  },
  Dashboard: {
    Overview: {
      ViewedPage: 'Dashboard.Overview.ViewedPage',
    },
    FirstTimeExperience: {
      Viewed: 'Dashboard.FirstTimeExperience.Viewed',
      AboutRequestsOpened: 'Dashboard.FirstTimeExperience.AboutRequestsOpened',
    },
    PrepareForRequest: {
      CardClicked: 'Dashboard.PrepareForRequest.CardClicked',
      CardActionCompleted: 'Dashboard.PrepareForRequest.CardActionCompleted',
      LearnMoreDialogOpened: 'Dashboard.PrepareForRequest.LearnMoreDialogOpened',
    },
  },
  Transparency: {
    Assessment: {
      StandardAssessmentViewedPage: 'Transparency.Assessment.StandardAssessmentViewedPage',
      BaselineAssessmentViewedPage: 'Transparency.Assessment.BaselineAssessmentViewedPage',
      LearnMoreDialogOpened: 'Transparency.Assessment.LearnMoreDialogOpened',
      AboutAssessmentsDialogOpened: 'Transparency.Assessment.AboutAssessmentsDialogOpened',
      ShareDataClicked: 'Transparency.Assessment.ShareDataClicked',
    },
    AiFileRepository: {
      FileUploaded: 'Transparency.AiFileRepository.FileUploaded',
      FilesSavedFromSubmission: 'Transparency.AiFileRepository.FilesSavedFromSubmission',
    },
    SourcingPromo: {
      Viewed: 'Transparency.SourcingPromo.Viewed',
      LearnMoreClicked: 'Transparency.SourcingPromo.LearnMoreClicked',
      Dismissed: 'Transparency.SourcingPromo.Dismissed',
    },
    SourcingInterest: {
      Submitted: 'Transparency.SourcingInterest.Submitted',
    },
    SourcingRecommendation: {
      RecommendationViewed: 'Transparency.SourcingRecommendation.RecommendationViewed',
      LearnMoreClicked: 'Transparency.SourcingRecommendation.LearnMoreClicked',
    },
  },
  Analyze: {
    Filter: {
      RiskAssessment: 'Analyze.Filter.RiskAssessment',
    },
    RiskAssessment: {
      Opened: 'Analyze.RiskAssessment.Opened',
      Saved: 'Analyze.RiskAssessment.Saved',
    },
    Assessment: {
      ViewedPage: 'Analyze.Assessment.ViewedPage',
      Toggled: 'Analyze.Assessment.Toggled',
      ExportedExcel: 'Analyze.Assessment.ExportedExcel',
      ExportedPDF: 'Analyze.Assessment.ExportedPDF',
      CategorizeFromAssessmentPages: 'Analyze.Assessment.Categorize',
      RiskAssessmentFromAssessmentPages: 'Analyze.Assessment.RiskAssessment',
    },
    CustomFlag: {
      Opened: 'Analyze.CustomFlag.Opened',
      Saved: 'Analyze.CustomFlag.Saved',
    },
    DataHub: {
      Insights: {
        ViewedInsights: 'Analyze.DataHub.Insights.ViewedInsights',
        ViewedInsightsModal: 'Analyze.DataHub.Insights.ViewedInsightsModal',
        PdfExport: 'Analyze.DataHub.Insights.PdfExport',
        LeftPage: 'Analyze.DataHub.Insights.LeftPage',
      },
    },
    SupplierRisk: {
      ViewedPage: 'Analyze.SupplierRisk.ViewedPage',
      AppliedFilter: 'Analyze.SupplierRisk.AppliedFilter',
      ViewedSupplierDetails: 'Analyze.SupplierRisk.ViewedSupplierDetails',
      CategorizeFromSupplierRiskPage: 'Analyze.SupplierRisk.CategorizeFromSupplierRiskPage',
      ClickedTopicRisk: 'Analyze.SupplierRisk.ClickedTopicRisk',
      ClickedRiskIndex: 'Analyze.SupplierRisk.ClickedRiskIndex',
      ClickedAssessment: 'Analyze.SupplierRisk.ClickedAssessment',
      ConfiguredFlags: 'Analyze.SupplierRisk.ConfiguredFlags',
    },
  },
  Report: {
    InsightsReport: {
      ViewedPage: 'Report.InsightsReport.ViewedPage',
      ReportItemCopied: 'Report.InsightsReport.ReportItemCopied',
    },
    DueDiligenceReport: {
      ViewedPage: 'Report.DueDiligenceReport.ViewedPage',
      DueDiligenceReportCopied: 'Report.DueDiligenceReport.DueDiligenceReportCopied',
    },
  },
}

export enum PrepareCardId {
  Assess = 'assess',
  Import = 'import',
  Share = 'share',
  DataAccess = 'dataAccess',
}

export enum PrepareCardAction {
  ResponseSubmitted = 'response_submitted',
  AssessmentViewed = 'assessment_viewed',
  FileUploaded = 'file_uploaded',
  InvitationSent = 'invitation_sent',
}

export enum TrackingSource {
  Dashboard = 'dashboard',
}

export const AmplitudeTrackingPages = {
  AssessmentComparePage: 'AssessmentComparePage',
  AssessmentInsightsDetails: 'AssessmentInsightsDetails',
  SupplierRiskImmediateAttention: 'SupplierRiskImmediateAttention',
  SupplierRiskFullscope: 'SupplierRiskFullscope',
}

export const APP_MAIN_SCROLL_ELEMENT_ID = 'app-main-scroll'
export const CONTENT_SCROLL_CONTAINER_ID = 'content-scroll-container'
